import { jsPDF } from "jspdf";
import QRCode from "qrcode";

// Fetch the LaneX logo once, resize to a compact 128x128 canvas, and cache
// the resulting data URL. This keeps the generated PDF under ~200 KB instead
// of embedding the original 130KB PNG uncompressed (which balloons to ~16 MB).
let _logoDataUrlCache = null;
async function fetchLogoDataUrl() {
  if (_logoDataUrlCache) return _logoDataUrlCache;
  try {
    const res = await fetch("/lanex-logo.png");
    if (!res.ok) return null;
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.crossOrigin = "anonymous";
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = objUrl;
    });
    // Downsize to 128×128 — the PDF renders it at 12 mm so this is plenty
    const target = 128;
    const canvas = document.createElement("canvas");
    canvas.width = target;
    canvas.height = target;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, target, target);
    _logoDataUrlCache = canvas.toDataURL("image/png");
    URL.revokeObjectURL(objUrl);
    return _logoDataUrlCache;
  } catch {
    return null;
  }
}

/**
 * Generate a print-ready LaneX Academy certificate as a PDF blob.
 * Landscape A4, obsidian dark background, cyan accents — matches the app aesthetic.
 */
export async function downloadCertificatePDF(cert) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const W = 297;
  const H = 210;

  const OBSIDIAN = [10, 10, 10];        // #0a0a0a
  const CARD = [20, 20, 20];            // #141414
  const CYAN = [0, 229, 255];           // #00e5ff — LaneX primary
  const LIME = [204, 255, 0];           // #ccff00 — accent
  const MUTED = [140, 140, 140];
  const TEXT = [235, 235, 235];

  // Full background
  doc.setFillColor(...OBSIDIAN);
  doc.rect(0, 0, W, H, "F");

  // Inner card
  const M = 10; // outer margin
  doc.setFillColor(...CARD);
  doc.roundedRect(M, M, W - 2 * M, H - 2 * M, 4, 4, "F");

  // Top-left brand: LaneX logo image (fallback to the stylised text tile if fetch fails)
  const logoDataUrl = await fetchLogoDataUrl();
  const logoX = M + 8;
  const logoY = M + 7;
  const logoSize = 12; // mm
  if (logoDataUrl) {
    try {
      // The 7th arg is `alias`, 8th is `compression` — FAST applies Flate
      doc.addImage(logoDataUrl, "PNG", logoX, logoY, logoSize, logoSize, "lanex-logo", "FAST");
    } catch {
      // If jsPDF can't decode the image for any reason, fall through to text tile
      doc.setFillColor(...TEXT);
      doc.roundedRect(logoX, logoY, logoSize, logoSize, 1, 1, "F");
    }
  } else {
    doc.setFillColor(...TEXT);
    doc.roundedRect(logoX, logoY, logoSize, logoSize, 1, 1, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...OBSIDIAN);
    doc.text("L", logoX + logoSize / 2, logoY + logoSize / 2 + 1.4, { align: "center" });
  }

  // Brand text — set to the right of the logo image
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...TEXT);
  doc.text("LaneX", logoX + logoSize + 4, logoY + 5.8);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("Academy", logoX + logoSize + 4, logoY + 10.6);

  // Top-right cert id
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(`// ${cert.cert_id}`, W - M - 8, M + 13, { align: "right" });

  // Section label
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...CYAN);
  doc.text("// CERTIFICATE OF COMPLETION", M + 16, M + 40);

  // "Awarded to"
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text("Awarded to", M + 16, M + 55);

  // Recipient name — big display
  doc.setFont("helvetica", "bold");
  doc.setFontSize(38);
  doc.setTextColor(...TEXT);
  doc.text(cert.user_name || "Learner", M + 16, M + 72);

  // Cyan underscore
  doc.setDrawColor(...CYAN);
  doc.setLineWidth(0.6);
  const nameWidth = Math.min(120, doc.getTextWidth(cert.user_name || "Learner"));
  doc.line(M + 16, M + 76, M + 16 + nameWidth, M + 76);

  // "for successfully completing"
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text("for successfully completing", M + 16, M + 90);

  // Path title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...CYAN);
  doc.text(cert.path_title || "Learning Path", M + 16, M + 104);

  // Blurb
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...TEXT);
  const blurb = "This achievement was earned through interactive lessons, adaptive quizzing, and mentor-led coaching on the LaneX Academy platform.";
  const blurbLines = doc.splitTextToSize(blurb, W - 2 * M - 32 - 60);
  doc.text(blurbLines, M + 16, M + 120);

  // Bottom row — Issued + Cert ID
  const rowY = H - M - 22;
  doc.setDrawColor(60, 60, 60);
  doc.setLineWidth(0.2);
  doc.line(M + 16, rowY - 4, W - M - 16, rowY - 4);

  doc.setFont("courier", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...MUTED);
  doc.text("ISSUED", M + 16, rowY);
  doc.text("CERTIFICATE ID", M + 80, rowY);
  doc.text("VERIFY AT", W - M - 80, rowY);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...TEXT);
  const issued = cert.issued_at ? new Date(cert.issued_at).toLocaleDateString() : "—";
  doc.text(issued, M + 16, rowY + 6);
  doc.setFont("courier", "bold");
  doc.text(cert.cert_id, M + 80, rowY + 6);

  const verifyUrl = `${window.location.origin}/verify/${cert.cert_id}`;
  doc.setFont("courier", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...CYAN);
  doc.text(verifyUrl, W - M - 80, rowY + 6);

  // QR code — right side
  try {
    const qrDataUrl = await QRCode.toDataURL(verifyUrl, {
      margin: 0,
      color: { dark: "#0a0a0a", light: "#ffffff" },
      width: 220,
    });
    const qrSize = 38;
    const qrX = W - M - 16 - qrSize;
    const qrY = M + 30;
    // White bezel behind QR
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 4, 1.5, 1.5, "F");
    doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrSize, qrSize);
    doc.setFont("courier", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text("SCAN TO VERIFY", qrX + qrSize / 2, qrY + qrSize + 5, { align: "center" });
  } catch { /* QR is nice-to-have */ }

  // Corner accent — lime L in bottom-right of card
  doc.setDrawColor(...LIME);
  doc.setLineWidth(0.8);
  doc.line(W - M - 4, H - M - 12, W - M - 4, H - M - 4);
  doc.line(W - M - 4, H - M - 4, W - M - 12, H - M - 4);

  // Anti-tamper legitimacy note — bottom-left, subtle but present
  doc.setFont("courier", "normal");
  doc.setFontSize(6.5);
  doc.setTextColor(...MUTED);
  doc.text(
    "This PDF is a public copy. Legitimacy is proven only by scanning the QR code,",
    M + 16, H - M - 8
  );
  doc.text(
    `which must verify at ${verifyUrl.replace(/^https?:\/\//i, "")} · edits invalidate the QR.`,
    M + 16, H - M - 5
  );

  doc.save(`LaneX-Certificate-${cert.cert_id}.pdf`);
}
